import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import RefreshIcon from "@mui/icons-material/Refresh";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { DashboardLayout } from "../../components/dashboard-layout";

const apiUrl = `${process.env.REACT_APP_API_URL}/mongo-admin`;
const pageSize = 20;
const managerTheme = createTheme({
    typography: {
        fontFamily: "'Plus Jakarta Sans', 'Segoe UI', sans-serif",
        button: { textTransform: "none", fontWeight: 600 },
    },
});

const requestConfig = () => {
    const token = localStorage.getItem("user");
    if (!token) throw new Error("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.");
    return { headers: { Authorization: `Bearer ${token}` } };
};

const getErrorMessage = (error) =>
    error.response?.data?.error?.message || error.message || "Không thể hoàn thành thao tác.";

const formatValue = (value) => {
    if (value === null || value === undefined) return "—";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

function MongoManager() {
    const [collections, setCollections] = useState([]);
    const [collectionName, setCollectionName] = useState("");
    const [selectedCollections, setSelectedCollections] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [loadingCollections, setLoadingCollections] = useState(true);
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [error, setError] = useState("");
    const [confirmation, setConfirmation] = useState(null);
    const [confirmationInput, setConfirmationInput] = useState("");
    const [working, setWorking] = useState(false);

    const selectedCollection = useMemo(
        () => collections.find((item) => item.name === collectionName),
        [collections, collectionName]
    );
    const writableCollections = useMemo(
        () => collections.filter((item) => item.type !== "view"),
        [collections]
    );
    const isReadOnly = selectedCollection?.type === "view";

    const fetchCollections = useCallback(async () => {
        setLoadingCollections(true);
        setError("");
        try {
            const response = await axios.get(`${apiUrl}/collections`, requestConfig());
            const nextCollections = response.data?.data || [];
            setCollections(nextCollections);
            setSelectedCollections((current) =>
                current.filter((name) =>
                    nextCollections.some((item) => item.name === name && item.type !== "view")
                )
            );
            setCollectionName((current) =>
                nextCollections.some((item) => item.name === current)
                    ? current
                    : nextCollections[0]?.name || ""
            );
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setLoadingCollections(false);
        }
    }, []);

    const fetchDocuments = useCallback(async (targetPage = page) => {
        if (!collectionName) {
            setDocuments([]);
            setTotal(0);
            setTotalPages(1);
            return;
        }

        setLoadingDocuments(true);
        setError("");
        try {
            const response = await axios.get(
                `${apiUrl}/collections/${encodeURIComponent(collectionName)}/documents`,
                { ...requestConfig(), params: { page: targetPage, limit: pageSize } }
            );
            const result = response.data?.data;
            setDocuments(result?.documents || []);
            setTotal(Number(result?.total) || 0);
            setTotalPages(Math.max(1, Number(result?.totalPages) || 1));
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setLoadingDocuments(false);
        }
    }, [collectionName, page]);

    useEffect(() => {
        fetchCollections();
    }, [fetchCollections]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const openConfirmation = (action) => {
        setConfirmation(action);
        setConfirmationInput("");
    };

    const closeConfirmation = () => {
        if (working) return;
        setConfirmation(null);
        setConfirmationInput("");
    };

    const executeAction = async () => {
        if (!confirmation || confirmationInput.trim() !== confirmation.phrase) return;
        setWorking(true);
        setError("");
        try {
            let bulkErrors = [];
            if (confirmation.kind === "document") {
                await axios.delete(
                    `${apiUrl}/collections/${encodeURIComponent(collectionName)}/documents/${encodeURIComponent(confirmation.documentId)}`,
                    { ...requestConfig(), data: { confirmation: confirmation.phrase } }
                );
            } else if (confirmation.kind === "clear") {
                await axios.delete(
                    `${apiUrl}/collections/${encodeURIComponent(collectionName)}/documents`,
                    { ...requestConfig(), data: { confirmation: confirmation.phrase } }
                );
            } else if (confirmation.kind === "bulk-clear") {
                const response = await axios.delete(`${apiUrl}/collections`, {
                    ...requestConfig(),
                    data: {
                        collectionNames: confirmation.collectionNames,
                        confirmation: confirmation.phrase,
                    },
                });
                bulkErrors = response.data?.data?.errors || [];
                setSelectedCollections([]);
            } else {
                await axios.delete(
                    `${apiUrl}/collections/${encodeURIComponent(collectionName)}`,
                    { ...requestConfig(), data: { confirmation: confirmation.phrase } }
                );
                setCollectionName("");
                setPage(1);
            }
            setConfirmation(null);
            setConfirmationInput("");
            await fetchCollections();
            if (confirmation.kind !== "drop") {
                const nextPage = confirmation.kind === "clear" || confirmation.kind === "bulk-clear"
                    ? 1
                    : page > 1 && documents.length === 1
                        ? page - 1
                        : page;
                setPage(nextPage);
                await fetchDocuments(nextPage);
            }
            if (bulkErrors.length) {
                setError(`Đã xóa được một phần. Không xóa được: ${bulkErrors.map((item) => item.name).join(", ")}.`);
            }
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setWorking(false);
        }
    };

    const dialogDescription = confirmation?.kind === "document"
        ? `Xóa vĩnh viễn document ${confirmation.documentId}?`
        : confirmation?.kind === "clear"
            ? `Xóa toàn bộ ${total} document trong collection "${collectionName}". Collection và index sẽ được giữ lại.`
            : confirmation?.kind === "bulk-clear"
                ? `Xóa tất cả document trong ${confirmation.collectionNames.length} collection dưới đây. Collection và index được giữ lại:\n${confirmation.collectionNames.join(", ")}`
                : `Xóa hẳn collection "${collectionName}" cùng tất cả dữ liệu và index.`;

    return (
        <ThemeProvider theme={managerTheme}>
            <DashboardLayout>
                <Box component="main" sx={{ py: 4, backgroundColor: "#F5F8FA", minHeight: "100vh" }}>
                    <Container maxWidth="xl">
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="h5" fontWeight={800}>Quản lý dữ liệu MongoDB</Typography>
                                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                                    Xem dữ liệu, xóa document hoặc xóa dữ liệu trong một hay nhiều collection. Thao tác xóa cần nhập đúng câu xác nhận.
                                </Typography>
                            </Box>

                            {error && <Alert severity="error">{error}</Alert>}

                            <Paper sx={{ p: 2 }}>
                                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                                    <FormControl fullWidth>
                                        <InputLabel id="mongo-collection-label">Collection</InputLabel>
                                        <Select
                                            labelId="mongo-collection-label"
                                            label="Collection"
                                            value={collectionName}
                                            disabled={loadingCollections || !collections.length}
                                            onChange={(event) => {
                                                setCollectionName(event.target.value);
                                                setPage(1);
                                            }}
                                        >
                                            {collections.map((collection) => (
                                                <MenuItem key={collection.name} value={collection.name}>
                                                    {collection.name} — {collection.type === "view" ? "view" : `${collection.count} bản ghi`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={fetchCollections}
                                        disabled={loadingCollections}
                                    >
                                        Tải lại
                                    </Button>
                                    {selectedCollection && !isReadOnly && (
                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ flexShrink: 0 }}>
                                            <Button
                                                color="warning"
                                                variant="outlined"
                                                startIcon={<DeleteOutlineIcon />}
                                                disabled={!total || loadingDocuments}
                                                onClick={() => openConfirmation({
                                                    kind: "clear",
                                                    phrase: `DELETE ALL ${collectionName}`,
                                                })}
                                            >
                                                Xóa dữ liệu
                                            </Button>
                                            <Button
                                                color="error"
                                                variant="contained"
                                                startIcon={<DeleteForeverIcon />}
                                                onClick={() => openConfirmation({
                                                    kind: "drop",
                                                    phrase: `DROP ${collectionName}`,
                                                })}
                                            >
                                                Xóa collection
                                            </Button>
                                        </Stack>
                                    )}
                                </Stack>
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={2}
                                    alignItems={{ md: "center" }}
                                    sx={{ mt: 2 }}
                                >
                                    <FormControl fullWidth>
                                        <InputLabel id="mongo-bulk-collections-label">Chọn nhiều collection để xóa dữ liệu</InputLabel>
                                        <Select
                                            labelId="mongo-bulk-collections-label"
                                            multiple
                                            value={selectedCollections}
                                            label="Chọn nhiều collection để xóa dữ liệu"
                                            disabled={loadingCollections || !writableCollections.length}
                                            onChange={(event) => setSelectedCollections(event.target.value)}
                                            renderValue={(selected) => selected.join(", ")}
                                        >
                                            {writableCollections.map((collection) => (
                                                <MenuItem key={collection.name} value={collection.name}>
                                                    <Checkbox checked={selectedCollections.includes(collection.name)} />
                                                    <ListItemText primary={collection.name} secondary={`${collection.count} bản ghi`} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Button
                                        variant="text"
                                        disabled={loadingCollections || !writableCollections.length}
                                        onClick={() => setSelectedCollections(
                                            selectedCollections.length === writableCollections.length
                                                ? []
                                                : writableCollections.map((collection) => collection.name)
                                        )}
                                        sx={{ flexShrink: 0 }}
                                    >
                                        {selectedCollections.length === writableCollections.length ? "Bỏ chọn tất cả" : "Chọn tất cả"}
                                    </Button>
                                    <Button
                                        color="error"
                                        variant="contained"
                                        startIcon={<DeleteForeverIcon />}
                                        disabled={!selectedCollections.length || working}
                                        onClick={() => {
                                            const collectionNames = [...selectedCollections].sort();
                                            openConfirmation({
                                                kind: "bulk-clear",
                                                collectionNames,
                                                phrase: `DELETE SELECTED ${collectionNames.length} COLLECTIONS`,
                                            });
                                        }}
                                        sx={{ flexShrink: 0 }}
                                    >
                                        Xóa dữ liệu đã chọn ({selectedCollections.length})
                                    </Button>
                                </Stack>
                                {selectedCollection?.type === "view" && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        Đây là view chỉ đọc; không thể xóa dữ liệu hoặc collection tại đây.
                                    </Alert>
                                )}
                            </Paper>

                            <Paper sx={{ overflow: "hidden" }}>
                                <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #EFF2F5" }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography fontWeight={700}>
                                            {collectionName || "Chưa chọn collection"}
                                        </Typography>
                                        {collectionName && <Chip size="small" label={`${total} bản ghi`} />}
                                    </Stack>
                                </Box>
                                <Box sx={{ overflowX: "auto" }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ minWidth: 210 }}>_id</TableCell>
                                                <TableCell>Nội dung document (trường nhạy cảm đã che)</TableCell>
                                                {!isReadOnly && <TableCell align="right">Thao tác</TableCell>}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {loadingDocuments ? (
                                                <TableRow>
                                                    <TableCell colSpan={isReadOnly ? 2 : 3} align="center" sx={{ py: 5 }}>
                                                        <CircularProgress size={26} />
                                                    </TableCell>
                                                </TableRow>
                                            ) : documents.length ? documents.map((document) => (
                                                <TableRow key={formatValue(document._id)} hover>
                                                    <TableCell sx={{ fontFamily: "monospace", verticalAlign: "top" }}>
                                                        {formatValue(document._id)}
                                                    </TableCell>
                                                    <TableCell sx={{ maxWidth: 780 }}>
                                                        <Box
                                                            component="pre"
                                                            sx={{
                                                                my: 0,
                                                                whiteSpace: "pre-wrap",
                                                                overflowWrap: "anywhere",
                                                                fontFamily: "monospace",
                                                                fontSize: 12,
                                                                maxHeight: 240,
                                                                overflow: "auto",
                                                            }}
                                                        >
                                                            {JSON.stringify(document, null, 2)}
                                                        </Box>
                                                    </TableCell>
                                                    {!isReadOnly && (
                                                        <TableCell align="right" sx={{ verticalAlign: "top" }}>
                                                            <Button
                                                                size="small"
                                                                color="error"
                                                                onClick={() => openConfirmation({
                                                                    kind: "document",
                                                                    documentId: formatValue(document._id),
                                                                    phrase: `DELETE ${formatValue(document._id)}`,
                                                                })}
                                                            >
                                                                Xóa
                                                            </Button>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            )) : (
                                                <TableRow>
                                                    <TableCell colSpan={isReadOnly ? 2 : 3} align="center" sx={{ py: 5 }}>
                                                        {collectionName ? "Collection đang trống." : "Chọn collection để xem dữ liệu."}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </Box>
                            </Paper>

                            <Stack direction="row" justifyContent="center">
                                <Pagination
                                    color="primary"
                                    page={page}
                                    count={totalPages}
                                    onChange={(_event, nextPage) => setPage(nextPage)}
                                    disabled={loadingDocuments || totalPages < 2}
                                />
                            </Stack>
                        </Stack>
                    </Container>
                </Box>
            </DashboardLayout>

            <Dialog open={Boolean(confirmation)} onClose={closeConfirmation} fullWidth maxWidth="sm">
                <DialogTitle sx={{ color: confirmation?.kind === "document" ? "error.main" : "error.main" }}>
                    {confirmation?.kind === "document"
                        ? "Xác nhận xóa document"
                        : confirmation?.kind === "clear"
                            ? "Xác nhận xóa toàn bộ dữ liệu"
                            : confirmation?.kind === "bulk-clear"
                                ? "Xác nhận xóa dữ liệu nhiều collection"
                            : "Xác nhận xóa collection"}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Alert severity="warning" sx={{ whiteSpace: "pre-line" }}>{dialogDescription}</Alert>
                        <Typography variant="body2">
                            Để xác nhận, nhập chính xác:
                        </Typography>
                        <Typography component="code" sx={{ fontFamily: "monospace", fontWeight: 800 }}>
                            {confirmation?.phrase}
                        </Typography>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Câu xác nhận"
                            value={confirmationInput}
                            onChange={(event) => setConfirmationInput(event.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirmation} disabled={working}>Hủy</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={executeAction}
                        disabled={working || confirmationInput.trim() !== confirmation?.phrase}
                    >
                        {working ? <CircularProgress size={20} color="inherit" /> : "Xác nhận xóa"}
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
}

export default MongoManager;
